/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Box, Typography } from '@mui/material'
import { HazardScenario } from '../types/hazard.types'
import {
  DotLegendEntry,
  SEDIMENT_DESCRIPTIONS,
  SEDIMENT_LABEL_MAP,
  SedimentLabel,
} from '../utils/hazardScenarioGrid.utils'
import { HazardSedimentPill } from './HazardSedimentPill'

const SEDIMENT_LABELS: SedimentLabel[] = ['NC', 'INC', 'RED']

const resolveSedimentLabel = (sediment: unknown): SedimentLabel =>
  SEDIMENT_LABEL_MAP[sediment == null ? '' : String(sediment)] ?? 'NC'

type HazardScenarioLegendProps = {
  scenarios: HazardScenario[]
  dotLegend: DotLegendEntry[] | null | undefined
}

export const HazardScenarioLegend = ({ scenarios, dotLegend }: HazardScenarioLegendProps) => {
  if (dotLegend != null) {
    if (dotLegend.length === 0) return null

    return (
      <Box data-testid="hazard-scenario-legend" css={rootStyles}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ whiteSpace: 'nowrap' }}
        >
          LEGEND
        </Typography>

        {dotLegend.map(({ key, label, color }) => (
          <Box key={key} css={itemStyles}>
            <Box
              css={css`
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: ${color};
                flex-shrink: 0;
              `}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    )
  }

  const presentLabels = SEDIMENT_LABELS.filter((label) =>
    scenarios.some((s) => resolveSedimentLabel(s.parameters['sediment']) === label),
  )

  const hasSedimentVariation = presentLabels.some((l) => l === 'INC' || l === 'RED')
  if (!hasSedimentVariation) return null

  return (
    <Box data-testid="hazard-scenario-legend" css={rootStyles}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        LEGEND:
      </Typography>

      {presentLabels.map((label) => (
        <Box
          key={label}
          data-testid={`hazard-scenario-legend-item-${label.toLowerCase()}`}
          css={itemStyles}
        >
          <HazardSedimentPill
            label={label}
            data-testid={`hazard-scenario-legend-pill-${label.toLowerCase()}`}
          />
          <Typography
            data-testid={`hazard-scenario-legend-description-${label.toLowerCase()}`}
            variant="caption"
            color="text.secondary"
          >
            {SEDIMENT_DESCRIPTIONS[label]}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

const rootStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  background-color: #fff;
  padding: 16px;
  border-radius: 8px;
`

const itemStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
`
